import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { initAllStores } from "../../utils/cache";
import { terminal } from "../../utils/user-helper";

interface InventorySection {
  name: string;
  count: number;
  available: number;
  unAvailable: number;
  color: string;
  categories: string[];
  path: string;
}

const InventoryPage = () => {
  const navigate = useNavigate();

    const [terminalId] = useState(() => terminal());

  const [sections, setSections] = useState<InventorySection[]>([
    {
      name: "Rooms",
      count: 0,
      available: 0,
      unAvailable: 0,
      color: "from-blue-500 to-blue-600",
      categories: [],
      path: "/inventory/rooms",
    },
    {
      name: "Drinks",
      count: 0,
      available: 0,
      unAvailable: 0,
      color: "from-purple-500 to-purple-600",
      categories: [],
      path: "/inventory/drinks",
    },
    {
      name: "Foods",
      count: 0,
      available: 0,
      unAvailable: 0,
      color: "from-green-500 to-green-600",
      categories: [],
      path: "/inventory/foods",
    },
    {
      name: "Games",
      count: 0,
      available: 0,
      unAvailable: 0,
      color: "from-pink-500 to-pink-600",
      categories: [],
      path: "/inventory/games",
    },
    {
      name: "Laundries",
      count: 0,
      available: 0,
      unAvailable: 0,
      color: "from-yellow-500 to-yellow-600",
      categories: [],
      path: "/inventory/laundries",
    },
  ]);

  useEffect(() => {
    const inventorySummary = async () => {
      const cache = await initAllStores();

      const foods = await cache.getAllBy("foods", { terminal: terminalId });
      const drinks = await cache.getAllBy("drinks", { terminal: terminalId });
      const games = await cache.getAllBy("games");
      const laundries = await cache.getAllBy("laundries");
      const rooms = await cache.getAllBy("rooms", { terminal: terminalId });

      setSections((prev: InventorySection[]) =>
        prev.map((sec) => {
          switch (sec.name) {
            case "Foods":
              return {
                ...sec,
                count: foods.length,
                available: foods.filter((f: any) => f.quantity > 0).length,
                unAvailable: foods.filter((d: any) => d.quantity <= 0).length,
                categories: [...new Set(foods.map((f: any) => f.foodCategory.name))],
              };
            case "Drinks":
              return {
                ...sec,
                count: drinks.length,
                available: drinks.filter((d: any) => d.quantity > 0).length,
                unAvailable: drinks.filter((d: any) => d.quantity <= 0).length,
                categories: [...new Set(drinks.map((d: any) => d.drinkCategory.name))],
              };
            case "Games":
              return {
                ...sec,
                count: games.length,
                available: games.filter((g: any) => g.isAvailable).length,
                unAvailable: games.filter((g: any) => !g.isAvailable).length,
                categories: [...new Set(games.map((g: any) => g.gameCategory.name))],
              };
            case "Laundries":
              return {
                ...sec,
                count: laundries.length,
                available: laundries.filter((l: any) => l.isAvailable).length,
                unAvailable: laundries.filter((l: any) => !l.isAvailable).length,
                categories: [...new Set(laundries.map((l: any) => l.laundryCategory.name))],
              };
            case "Rooms":
              return {
                ...sec,
                count: rooms.length,
                available: rooms.filter((r: any) => !r.isOccupied).length,
                unAvailable: rooms.filter((r: any) => r.isOccupied).length,
                categories: [...new Set(rooms.map((r: any) => r.name))],
              };
            default:
              return sec;
          }
        })
      );
    };

    inventorySummary();
  }, []);

  return (
    <div className="flex-1 p-4 md:p-6 space-y-6 overflow-y-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Inventory</h1>
      </div>

      {/* Inventory Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {sections.map((section) => (
          <div
            key={section.name}
            className="bg-white shadow-md rounded-2xl p-5 hover:shadow-lg transition cursor-pointer"
            onClick={() => navigate(section.path)}
          >
            {/* Stat Header */}
            <div
              className={`bg-gradient-to-r ${section.color} text-white rounded-xl p-4 mb-4`}
            >
              <h2 className="text-lg font-semibold">{section.name}</h2>
              <p className="text-2xl font-bold">{section.count}</p>
              {/* {(section.name === "Rooms" || section.name === "Games") && ( */}
              <p className="text-sm opacity-80">
                {section.available > 0 && `${section.available} Available`}

                {section.available > 0 && section.unAvailable > 0 && ", "}

                {section.unAvailable > 0 && (
                  section.name === "Rooms"
                    ? `${section.unAvailable} Occupied`
                    : section.name === "Drinks" || section.name === "Foods"
                      ? `${section.unAvailable} Out of Stock`
                      : `${section.unAvailable} Unavailable`
                )}
              </p>


            </div>

            {/* Categories */}
            <div>
              <p className="text-gray-600 font-medium mb-2">Categories</p>
              <div className="flex flex-wrap gap-2">
                {section.categories.length > 0 ? (
                  section.categories.map((cat) => (
                    <span
                      key={cat}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs"
                    >
                      {cat}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-400 text-sm">No categories</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Insights Section */}
      <div className="bg-white shadow-md rounded-2xl p-6">
        <h2 className="text-lg font-semibold mb-3">Inventory Insights</h2>
        <p className="text-gray-600">
          Select an inventory section above to view detailed items, update stock,
          and track usage.
        </p>
      </div>
    </div>
  );
};

export default InventoryPage;